/**
 * The application header.
 *
 * Two presentations of ONE menu definition (`NAV_GROUPS`): an inline row of
 * dropdowns on a wide viewport, and a drawer of always-open sections below
 * `DESKTOP_QUERY`. Which one renders is decided by matchMedia, not by CSS
 * hiding one of them, so only one copy of the menu is ever in the DOM.
 *
 * It no longer uses the template's `data-toggle="dropdown"`. That handed the
 * menus to the legacy jQuery/Bootstrap bundle in public/index.html, which put
 * the open panel INSIDE whatever box the trigger sat in — and the small-screen
 * header used to be a horizontally scrolling strip (`overflow-x: auto`, which
 * makes the other axis `auto` too), so every menu opened clipped to a ~34px
 * tall box. The drawer exists because a menu you cannot see is not responsive.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './styles/header.css';
import Logo from '../../Atoms/Logo.jsx';
import { useSwal } from '../../Notifications/swal/SwalCom';
import IconButton from '../../Atoms/IconButton.jsx';
import { LANGUAGES, useI18n } from '../../../i18n';

/** Above this the menu is a row of dropdowns; below it, a drawer. */
const DESKTOP_QUERY = '(min-width: 768px)';

/**
 * Viewport test as state. Falls back to desktop where matchMedia is missing
 * (jsdom), so a test that renders the header still gets the full menu.
 */
const useIsDesktop = () => {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [isDesktop, setIsDesktop] = useState(() =>
    (supported ? window.matchMedia(DESKTOP_QUERY).matches : true),
  );

  useEffect(() => {
    if (!supported) return undefined;
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event) => setIsDesktop(event.matches);
    setIsDesktop(query.matches);
    // Safari < 14 only has the deprecated listener API.
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);
      else query.removeListener(onChange);
    };
  }, [supported]);

  return isDesktop;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { swalConfirm } = useSwal();
  const { t, lang, changeLanguage } = useI18n();
  const isDesktop = useIsDesktop();

  const [userName, setUserName] = useState('');
  const [theme, setTheme] = useState(() => (localStorage.getItem('lf-theme') === 'light' ? 'light' : 'dark'));
  // One id at a time: a group id, 'user', or null. Two open menus is never right.
  const [openMenu, setOpenMenu] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const name = localStorage.getItem('name') || 'User';
    setUserName(name);
  }, []);

  /**
   * Publish the bar's real height as `--lf-header-h`.
   *
   * A full-height page (`.lf-fill-viewport`) has to subtract the chrome above
   * it, and every page that guessed the number guessed differently and wrongly
   * — 90px in the labeling studios, 100px in Prediction, 58px in the assistant,
   * against a bar that measures 57px and re-wraps at some widths. Measuring it
   * is the only version that cannot drift. The drawer is absolutely positioned,
   * so opening it does not change what is published here.
   */
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const write = () => {
      root.style.setProperty('--lf-header-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    };
    write();
    if (typeof ResizeObserver === 'undefined') {
      // jsdom, and very old browsers: the CSS fallback covers it.
      window.addEventListener('resize', write);
      return () => window.removeEventListener('resize', write);
    }
    const observer = new ResizeObserver(write);
    observer.observe(el);
    return () => {
      observer.disconnect();
      // Login/Signup render outside this shell; leaving a stale value behind
      // would be a number describing a header that is no longer on screen.
      root.style.removeProperty('--lf-header-h');
    };
  }, []);

  const NAV_GROUPS = useMemo(
    () => [
      {
        id: 'management',
        label: t('nav.management'),
        items: [
          { to: '/Assistant', label: t('nav.aiAssistant'), icon: 'fa-comments' },
          { to: '/ProjectManagement', label: t('nav.projectManagement'), icon: 'fa-folder-open' },
          { to: '/ModelManagement', label: t('nav.modelManagement'), icon: 'fa-cube' },
        ],
      },
      {
        id: 'datasetPrep',
        label: t('nav.datasetPrep'),
        items: [{ to: '/VideoToImages', label: t('nav.videoToImages'), icon: 'fa-film' }],
      },
      {
        // Prediction was ROUTED but not reachable: `/Prediction` existed in
        // App.js and the only link to it was a quick-link tile on the assistant
        // welcome screen, which is itself only visible before a conversation
        // starts. A page nobody can navigate to reads as a page that does not
        // exist.
        id: 'inference',
        label: t('nav.inference'),
        items: [{ to: '/Prediction', label: t('nav.prediction'), icon: 'fa-crosshairs' }],
      },
      {
        id: 'publicHub',
        label: t('nav.publicHub'),
        items: [{ to: '/ModelHub', label: t('nav.modelHub'), icon: 'fa-globe' }],
      },
    ],
    [t],
  );

  const closeAll = useCallback(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
  }, []);

  // Leaving the page closes whatever was open — otherwise the menu is still
  // hanging over the screen the user just navigated to.
  useEffect(() => {
    closeAll();
  }, [location.pathname, closeAll]);

  // Crossing the breakpoint swaps the whole presentation; anything open belongs
  // to the presentation that just went away.
  useEffect(() => {
    closeAll();
  }, [isDesktop, closeAll]);

  useEffect(() => {
    if (!openMenu && !drawerOpen) return undefined;
    const onPointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) closeAll();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeAll();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu, drawerOpen, closeAll]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.classList.remove('dark', 'light');
    document.documentElement.classList.add(next);
    localStorage.setItem('lf-theme', next);
    setTheme(next);
  };

  // EN <-> KO. The choice persists in localStorage under `lf-lang`, mirroring
  // how the theme toggle persists `lf-theme`.
  const toggleLanguage = () => {
    const index = LANGUAGES.findIndex((item) => item.code === lang);
    changeLanguage(LANGUAGES[(index + 1) % LANGUAGES.length].code);
  };

  const handleLogout = () => {
    closeAll();
    swalConfirm(t('nav.logout'), t('nav.logoutConfirm'), (result) => {
      if (result?.isConfirmed) {
        localStorage.clear();
        navigate('/Login');
      }
    });
  };

  const go = (to) => {
    closeAll();
    navigate(to);
  };

  const isCurrent = useCallback(
    (to) => location.pathname.toLowerCase() === to.toLowerCase(),
    [location.pathname],
  );

  const renderItem = (item) => (
    <li key={item.to}>
      <button
        type="button"
        className={`lf-nav__item${isCurrent(item.to) ? ' is-current' : ''}`}
        onClick={() => go(item.to)}
        aria-current={isCurrent(item.to) ? 'page' : undefined}
      >
        <i className={`fa ${item.icon} lf-nav__item-icon`} aria-hidden="true" />
        <span>{item.label}</span>
      </button>
    </li>
  );

  return (
    <header className="pcoded-header header-labelflow" ref={headerRef}>
      <div className="lf-header__bar">
        {!isDesktop && (
          <button
            type="button"
            className="lf-nav-toggle"
            aria-expanded={drawerOpen}
            aria-controls="lf-primary-nav"
            aria-label={drawerOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            title={drawerOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            onClick={() => {
              setOpenMenu(null);
              setDrawerOpen((open) => !open);
            }}
          >
            <i className={`fa ${drawerOpen ? 'fa-times' : 'fa-bars'}`} aria-hidden="true" />
          </button>
        )}

        <button
          type="button"
          className="lf-brand"
          onClick={() => go('/Assistant')}
          aria-label={t('nav.home')}
        >
          <Logo size={26} />
        </button>

        {isDesktop && (
          <nav className="lf-nav" id="lf-primary-nav" aria-label={t('nav.primary')}>
            <ul className="lf-nav__list">
              {NAV_GROUPS.map((group) => {
                const open = openMenu === group.id;
                const groupIsCurrent = group.items.some((item) => isCurrent(item.to));
                return (
                  <li className="lf-nav__group" key={group.id}>
                    <button
                      type="button"
                      className={`lf-nav__trigger${open ? ' is-open' : ''}${groupIsCurrent ? ' is-current' : ''}`}
                      aria-expanded={open}
                      aria-haspopup="true"
                      aria-controls={`lf-nav-panel-${group.id}`}
                      onClick={() => setOpenMenu(open ? null : group.id)}
                    >
                      <span>{group.label}</span>
                      <i className="fa fa-chevron-down lf-nav__caret" aria-hidden="true" />
                    </button>
                    <div
                      className="lf-nav__panel"
                      id={`lf-nav-panel-${group.id}`}
                      hidden={!open}
                    >
                      <ul className="lf-nav__panel-list">{group.items.map(renderItem)}</ul>
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        <div className="lf-header__actions">
          <button
            type="button"
            className="lf-lang-btn"
            onClick={toggleLanguage}
            title={t('common.language')}
            aria-label={t('common.language')}
          >
            {(LANGUAGES.find((item) => item.code === lang) || LANGUAGES[0]).short}
          </button>
          <IconButton
            icon={theme === 'dark' ? 'fa-sun' : 'fa-moon'}
            onClick={toggleTheme}
            title={theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')}
          />
          <div className="lf-user">
            <button
              type="button"
              className={`lf-icon-btn lf-user__trigger${openMenu === 'user' ? ' is-open' : ''}`}
              aria-expanded={openMenu === 'user'}
              aria-haspopup="true"
              aria-controls="lf-user-panel"
              aria-label={t('nav.account')}
              title={t('nav.account')}
              onClick={() => setOpenMenu(openMenu === 'user' ? null : 'user')}
            >
              <i className="fa fa-user" aria-hidden="true" />
            </button>
            <div className="lf-nav__panel lf-user__panel" id="lf-user-panel" hidden={openMenu !== 'user'}>
              <p className="lf-user__name">{userName}</p>
              <ul className="lf-nav__panel-list">
                <li>
                  <button type="button" className="lf-nav__item" onClick={handleLogout}>
                    <i className="fa fa-sign-out-alt lf-nav__item-icon" aria-hidden="true" />
                    <span>{t('nav.logout')}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* No scrim element: theme-overrides gives the header `backdrop-filter`,
          which makes it the containing block for fixed descendants, so a
          `position: fixed` overlay rendered in here would be trapped inside the
          bar instead of covering the page. Tapping away is handled by the
          document listener above. */}
      {!isDesktop && (
        <nav
          className="lf-drawer"
          id="lf-primary-nav"
          aria-label={t('nav.primary')}
          hidden={!drawerOpen}
        >
          {NAV_GROUPS.map((group) => (
            <section className="lf-drawer__section" key={group.id}>
              <h2 className="lf-drawer__title">{group.label}</h2>
              <ul className="lf-nav__panel-list">{group.items.map(renderItem)}</ul>
            </section>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Header;
